import { Injectable, inject } from '@angular/core';

import { debounceTime, filter, fromEvent, Subject, Subscription } from 'rxjs';

import { DomEventsStreams, DomEventsSubjects, KeyNames } from '@app/types/dom-events.types';
import { WINDOW } from '@app/common/tokens/window.token';

@Injectable({
  providedIn: 'root'
})
export class DomEventsService {
  private window = inject<Window>(WINDOW);

  eventStreams: DomEventsStreams = {};

  private subjects: DomEventsSubjects = {
    windowBeforeUnload: new Subject<BeforeUnloadEvent>(),
    windowResize: new Subject<Event>(),
    escapeKeyDown: new Subject<KeyboardEvent>(),
    enterKeyDown: new Subject<KeyboardEvent>()
  };

  private keyboardEventListenerSubscription: Subscription | null = null;

  private readonly RESIZE_EVENT_DEBOUNCE_TIME = 1000;

  private readonly allowedKeys: string[] = [KeyNames.Escape, KeyNames.Enter, KeyNames.NumpadEnter, KeyNames.ArrowUp, KeyNames.ArrowDown, KeyNames.ArrowLeft, KeyNames.ArrowRight];

  init(): void {
    this.createDOMEventListeners();
    this.createEventStreamsObject();
  }

  pauseKeyboardEventListener(): void {
    if (this.keyboardEventListenerSubscription) {
      this.keyboardEventListenerSubscription.unsubscribe();
      this.keyboardEventListenerSubscription = null;
    }
  }

  resumeKeyboardEventListener(): void {
    if (!this.keyboardEventListenerSubscription) {
      this.keyboardEventListenerSubscription = this.createKeyboardEventListener();
    }
  }

  private createDOMEventListeners(): void {
    fromEvent(this.window, 'resize')
      .pipe(debounceTime(this.RESIZE_EVENT_DEBOUNCE_TIME))
      .subscribe((event) => this.subjects.windowResize.next(event));

    fromEvent(this.window, 'beforeunload').subscribe((event) => this.subjects.windowBeforeUnload.next(event));

    this.keyboardEventListenerSubscription = this.createKeyboardEventListener();
  }

  private createKeyboardEventListener(): Subscription {
    return fromEvent(this.window, 'keydown')
      .pipe(filter((event) => this.shouldHandleKeyEvent(event)))
      .subscribe((event: Event) => {
        const keyEvent = event as KeyboardEvent;

        // Escape
        if (keyEvent.code === KeyNames.Escape) {
          this.subjects.escapeKeyDown.next(keyEvent);
          return;
        }

        // Enter key
        if (keyEvent.code === KeyNames.Enter || keyEvent.code === KeyNames.NumpadEnter) {
          this.subjects.enterKeyDown.next(keyEvent);
          return;
        }
      });
  }

  private createEventStreamsObject(): void {
    this.eventStreams = {
      windowBeforeUnload: this.subjects.windowBeforeUnload.asObservable(),
      windowResize: this.subjects.windowResize.asObservable(),
      escapeKeyDown: this.subjects.escapeKeyDown.asObservable(),
      enterKeyDown: this.subjects.enterKeyDown.asObservable()
    };
  }

  /**
   * Determines if a keyboard event should be handled by checking:
   * 1. If it's not a repeat event (e.g., holding down a key)
   * 2. If it’s not a modifier key and not included in the allowedKey list
   * 3. If it's not coming from an input, textarea, or contentEditable element
   */
  private shouldHandleKeyEvent(event: Event): boolean {
    const keyEvent = event as KeyboardEvent;

    // Ignore repeat events
    if (keyEvent.repeat) {
      return false;
    }

    const isModifierKey = keyEvent.ctrlKey || keyEvent.metaKey || keyEvent.altKey;
    const isAllowedKey = this.allowedKeys.includes(keyEvent.code);

    // Ignore keys we don't handle
    if (!isModifierKey && !isAllowedKey) {
      return false;
    }

    const element = keyEvent.target as HTMLElement;

    if (keyEvent.altKey) {
      // For alt key, we only block if it's a contentEditable element (Froala).
      // This allows alt-shortcuts in text inputs and textareas.
      return !element.isContentEditable;
    }

    return !(element.isContentEditable || ('type' in element && (element as HTMLInputElement).type === 'text'));
  }
}
